import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
} from '@mui/material';
import { DotsThreeVertical } from '@phosphor-icons/react';
import { useThemeStyles } from '../../theme/useThemeStyles';

function DashboardCard({
  title,
  icon,
  iconBadgeCount = null,
  iconBadgeMax = 99,
  loading = false,
  error = null,
  emptyMessage = 'Nothing to show here yet.',
  emptyIcon: EmptyIcon = null,
  isEmpty = false,
  onClick,
  menuItems = [],
  headerAction = null,
  iconBackgroundColor = 'rgba(124, 58, 237, 0.12)',
  iconColour = 'rgba(124, 58, 237, 1)',
  children,
  sx = {},
}) {
  const styles = useThemeStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const primaryAction = headerAction || (menuItems.length === 1 ? menuItems[0] : null);
  const showOverflowMenu = menuItems.length > 1;

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action) => {
    if (action && typeof action === 'function') {
      action();
    }
    handleMenuClose();
  };

  return (
    <Card
      sx={{
        height: sx.height || sx.minHeight || '100%',
        minHeight: sx.minHeight || '400px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(255, 255, 255, 0.72)',
        backdropFilter: 'blur(6px)',
        cursor: 'default',
        transition: 'all 0.3s ease',
        // Translucent background is now handled globally by theme
        color: 'rgba(0, 0, 0, 1)',
        '@media (max-width:600px)': {
          minHeight: 'unset',
          maxHeight: '400px',
          overflow: 'scroll',
        },
        ...sx,
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          pt: 2,
          pb: 1,
        }}
      >
        <Box 
          sx={{
            ...styles.layout.flexRow,
            gap: 1.5,
            alignItems: 'center',
            flex: 1,
          }}
        >
          {icon && (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                backgroundColor: iconBackgroundColor,
                color: iconColour,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                position: 'relative',
                overflow: 'visible',
              }}
            >
              {icon}
              {typeof iconBadgeCount === 'number' && iconBadgeCount > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    minWidth: 16,
                    height: 16,
                    px: iconBadgeCount > 9 ? 0.4 : 0.3,
                    borderRadius: 999,
                    backgroundColor: '#d32f2f',
                    color: '#fff',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.95)',
                  }}
                >
                  {iconBadgeCount > iconBadgeMax ? `${iconBadgeMax}+` : iconBadgeCount}
                </Box>
              )}
            </Box>
          )}
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: 400,
              fontSize: '0.9375rem', // 15px - slightly reduced
              letterSpacing: 0,
            }}
          >
            {title}
          </Typography>
        </Box>
        {primaryAction && (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              primaryAction.onClick?.();
            }}
            sx={{
              ml: 1,
              minWidth: 'fit-content',
              borderColor: 'rgba(0, 0, 0, 0.1)',
              color: 'text.primary',
              borderRadius: '10px',
              fontSize: '0.78rem',
              fontWeight: 500,
              px: 1.25,
              py: 0.375,
              textTransform: 'none',
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.2)',
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              },
            }}
          >
            {primaryAction.label}
          </Button>
        )}
        {showOverflowMenu && (
          <>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClick(e);
              }}
              sx={{ ml: 'auto' }}
            >
              <DotsThreeVertical size={20} weight="bold" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              onClick={(e) => e.stopPropagation()}
            >
              {menuItems.map((item, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleMenuItemClick(item.onClick)}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
      <CardContent sx={{ flex: 1, pt: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="rectangular" height={60} />
          </Box>
        ) : error ? (
          <Box sx={styles.patterns.emptyState}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        ) : isEmpty || !children || (Array.isArray(children) && children.length === 0) ? (
          <Box sx={styles.patterns.emptyState}>
            {EmptyIcon && (
              <Box
                sx={{
                  mb: 2,
                  mx: 'auto',
                  width: 92,
                  height: 92,
                  borderRadius: '50%',
                  backgroundColor: iconBackgroundColor,
                  color: iconColour,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <EmptyIcon size={40} weight="regular" />
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

export default DashboardCard;
